import {
    ModalToolContainerService,
    WebmateToolConfig
} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service';
import {AuditId, ProjectId} from '@webmate-web-core/shared/types/ids';
import {Component, Inject, OnInit} from '@angular/core';
import {TOOL_CONTAINER_DATA} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.component';
import {
    faCheck,
    faChevronRight,
    faPlay,
    faPlus,
    faSyncAlt,
    faTrash
} from '@fortawesome/pro-solid-svg-icons';
import {FormBuilder, UntypedFormGroup} from '@angular/forms';
import {faSave} from '@fortawesome/pro-light-svg-icons';
import {
    AssessmentTask,
    AuditActivitiesFilterSpec,
    AuditService,
    HydratedAuditTarget
} from '@webmate-web-core/shared/services/audit.service';
import {AuditReportConfig, SectionGeneratorConfig, SectionGeneratorId} from '@webmate-web-core/shared/types/audit';
import {SelectItem} from 'primeng/api';
import {switchMap} from 'rxjs';
import {map, mergeMap} from 'rxjs/operators';
import {FindingFilterSpec, FindingSeverity, Spec, WellknownSeverities} from '@webmate-web-core/shared/types/testmgmt';
import {TestService} from '@webmate-web-core/shared/services/test.service';
import {AuditRequirement} from '@webmate-web-core/shared/tools/audit/audittemplate/shared';
import {BrowserSessionStateArtifact} from '@webmate-web-core/shared/types/browsersession';
import {data} from 'jquery';
import {
    PreviewReportToolComponent,
    PreviewReportToolConfig
} from '@webmate-web-core/projects/project/auditlab/audits/preview-report-tool.component';
import {capitalize} from '@webmate-web-core/shared/utils/utils';
import {ReportTemplate} from '../../../../../report/types';
import {ReportService} from '@webmate-web-core/shared/services/report.service';
import {
    CreateReportTemplateToolComponent,
    CreateReportTemplateToolConfig
} from '@webmate-web-core/projects/project/auditlab/audits/create-report-template-tool.component';
import {makeFormFromSectionGeneratorConfig} from '@webmate-web-core/projects/project/auditlab/audits/report-section-generators.component';
import {ReportExpr} from '../../../../../report/report_types';
import {FeatureService} from '@webmate-web-core/shared/services/feature.service';

export class CreateAuditReportToolConfig implements WebmateToolConfig {
    scrollable = true;

    /**
     * @param toolName
     * @param auditId
     * @param projectId
     * @param createConfigMode if true, the tool will be in configuration mode, allowing the user to create a new report configuration, but does not create a report yet.
     * @param auditReportParameters
     * @param onCreateReportConfig
     * @param submitLabel
     */
    constructor(public toolName: string = 'Configure Audit Report', public auditId: AuditId, public projectId: ProjectId, public createConfigMode: boolean = false,
                public auditReportParameters: {[name: string]: ReportExpr} = {}, public onCreateReportConfig: (config: AuditReportConfig, templateName: string) => void, public submitLabel?: string) {}
}

@Component({
    selector: 'wm-create-auditreport-tool',
    templateUrl: './create-auditreport-tool.component.html',
    styleUrls: ['./create-auditreport-tool.component.less']
})
export class CreateAuditReportToolComponent implements OnInit {

    form: UntypedFormGroup;

    preview: any;

    baseTemplates: ReportTemplate[];
    customTemplates: ReportTemplate[];
    loading: boolean = true;
    creatingReport: boolean = false;

    configurationHidden: boolean = true;
    activitiesFilters: { [generatorId: string]: AuditActivitiesFilterSpec } = {};

    assessmentTasks: AssessmentTask[] = [];
    testObjects: HydratedAuditTarget[];
    auditRequirements: AuditRequirement[];
    idToAssessmentTask: { [id: string]: AssessmentTask } = {};
    statesByTestObjectId: { [testObjectId: string]: BrowserSessionStateArtifact } = {};
    testObjectsById: { [testObjectId: string]: HydratedAuditTarget } = {};
    specs: { [specId: string]: Spec } = {};
    specsList: Spec[] = [];
    states: { [stateId: string]: BrowserSessionStateArtifact } = {};

    availableSeverities: SelectItem[];
    selectedFindingSeverities: FindingSeverity[];

    activitiesLoading: boolean = false;
    submitLabel: string;
    infoMessage: string;

    selectedTemplate?: string;
    activeConfiguration?: AuditReportConfig;

    allowCustomReports: boolean = false;

    constructor(@Inject(TOOL_CONTAINER_DATA) public config: CreateAuditReportToolConfig,
                private fb: FormBuilder,
                private auditService: AuditService,
                private testService: TestService,
                private toolService: ModalToolContainerService,
                private reportService: ReportService,
                private featureService: FeatureService) {

            this.availableSeverities = WellknownSeverities.map(s => {
                if (s === 'not_set') {
                    return {label: 'Not Set', value: 'not_set'};
                }
                return {label: capitalize(s), value: s};
        });

        // Default to error and warn
        this.selectedFindingSeverities = this.availableSeverities.filter(i => i.value === 'error' || i.value === 'warn').map(i => i.value);
        this.submitLabel = config.submitLabel ? config.submitLabel : 'Generate Report';
    }

    ngOnInit() {
        this.featureService.getFeature("webmate.report-customization").subscribe(feature => this.allowCustomReports = feature?.isActive || false);

        this.reportService.getReportTemplatesForProject$(this.config.projectId, 'audit').subscribe(templates => {
            this.baseTemplates = templates.filter(t => t.isBaseTemplate);
            this.customTemplates = templates.filter(t => !t.isBaseTemplate);
            this.loading = false;
        });

        this.auditService.getAudit$(this.config.auditId).pipe(
            switchMap(audit => {
                return this.auditService.queryAuditActivitiesOfAudit$(this.config.auditId, {}).pipe(map(activities => ({audit, activities})));
            }),
            switchMap(({audit, activities}) => {
                this.assessmentTasks = activities;
                this.idToAssessmentTask = activities.reduce((acc, task) => {
                    acc[task.id] = task;
                    return acc;
                }, {} as { [id: string]: AssessmentTask });

                this.testObjects = audit.workflow.spec.testObjects || [];
                this.auditRequirements = audit.workflow.spec.requirements;
                return this.testService.getSpecs$(this.auditRequirements.map(req => req.specId)).pipe(map(specs => ({audit, activities, specs})));
            }),
            mergeMap(({audit, activities, specs}) => {
                const testObjectIds = (this.testObjects || []).map(obj => obj.testObjectId);
                return this.auditService.getRepresentativeStatesByTestObjectIds$(testObjectIds).pipe(
                    map(statesByTestObjectId => ({audit, activities, specs, statesByTestObjectId}))
                );
            })
        ).subscribe(({audit, specs, activities, statesByTestObjectId}) => {
            this.specsList = specs;
            this.specs = specs.reduce((acc, spec) => {
                acc[spec.id] = spec;
                return acc;
            }, {} as { [specId: string]: Spec });

            this.statesByTestObjectId = this.testObjects!.reduce((acc: any, obj) => {
                const targetId = obj.testObjectId;
                const state = statesByTestObjectId[targetId];
                if (state) {
                    acc[obj.testObjectId] = state;
                }
                return acc;
            }, {});

            this.states = Object.values(this.statesByTestObjectId).reduce((acc: any, state) => {
                acc[state.id] = state;
                return acc;
            }, {});

            this.testObjectsById = this.testObjects!.reduce((acc, obj) => {
                acc[obj.testObjectId] = obj;
                return acc;
            }, {} as { [testObjectId: string]: HydratedAuditTarget });

            this.activitiesLoading = false;
        })


        this.form = this.fb.group({
            'name': [''],
            'description': [''],
            'templateId': ['']
        });
        this.reset();
    }

    makeFormFromSectionGeneratorConfig(config: SectionGeneratorConfig): UntypedFormGroup {
        return makeFormFromSectionGeneratorConfig(this.fb, config);
    }

    toggleConfigurationHidden() {
        this.configurationHidden = !this.configurationHidden;
    }

    onActivitiesFilterChanged(event: { generatorId: string; filter: AuditActivitiesFilterSpec }): void {
        this.activitiesFilters[event.generatorId] = event.filter;
    }

    makeFormFromConfig(config: AuditReportConfig): UntypedFormGroup {
        const form: any = {
            'name': [config.reportName],
            'description': [config.description],
            'parameters': this.populateParameters(config),
            'subsections': this.fb.group({})
        };

        for (const section of config.sectionGenerators) {
            form.subsections.addControl(section.id, this.makeFormFromSectionGeneratorConfig(section));
        }

        return this.fb.group(form);
    }

    private populateParameters(reportConfig: AuditReportConfig): { [key: string]: ReportExpr }[] {
        for (const key in reportConfig.parameters) {
            if (!!this.config.auditReportParameters[key]) {
                reportConfig.parameters[key] = this.config.auditReportParameters[key];
            }
        }
        return [reportConfig.parameters || {}]
    }

    makeSectionGeneratorConfigFromForm(sectionId: SectionGeneratorId, form: any): SectionGeneratorConfig {
        if (form.type === 'ActivitiesSectionGeneratorConfig') {
            let findingsFilter: FindingFilterSpec = {severityFilterSpec: this.selectedFindingSeverities};
            return {
                type: 'ActivitiesSectionGeneratorConfig',
                id: sectionId,
                title: form.title,
                activitiesQuery: this.activitiesFilters[sectionId] || form.activitiesQuery,
                findingsFilter,
                grouping: form.grouping ? [form.grouping] : undefined,
                includedStats: form.includedStats,
                includeOverviewSection: form.includeOverviewSection,
                styleClasses: form.styleClasses,
            };
        } else if (form.type === 'RequirementResultMatrixSectionGeneratorConfig') {
            return {
                type: 'RequirementResultMatrixSectionGeneratorConfig',
                id: sectionId,
                title: form.title,
                activitiesQuery: this.activitiesFilters[sectionId] || form.activitiesQuery,
                includeLegend: form.includeLegend,
                caption: form.caption,
                styleClasses: form.styleClasses,
            };
        } else if (form.type === 'StaticSectionGeneratorConfig') {
            const subsections: SectionGeneratorConfig[] = [];
            for (const subsectionId in form.subsections) {
                const subsectionForm = form.subsections[subsectionId];
                subsections.push(this.makeSectionGeneratorConfigFromForm(subsectionId, subsectionForm));
            }

            return {
                type: 'StaticSectionGeneratorConfig',
                id: sectionId,
                title: form.title,
                content: form.content,
                subsections: subsections,
                styleClasses: form.styleClasses
            };
        } else if (form.type === 'TableOfContentsGeneratorConfig') {
            return {
                type: 'TableOfContentsGeneratorConfig',
                id: sectionId,
                title: form.title,
                styleClasses: form.styleClasses
            };
        } else {
            console.error('Unknown section generator type', form.type);
            throw new Error("Unknown section generator type");
        }
    }

    makeConfigFromForm(form: UntypedFormGroup): AuditReportConfig {
        const config: AuditReportConfig = {
            reportName: form.value.name,
            description: form.value.description,
            sectionGenerators: [],
            parameters: form.value.parameters || {},
            renderingConfig: this.activeConfiguration?.renderingConfig
        }

        for (const sectionId in form.value.subsections) {
            const sectionForm = form.value.subsections[sectionId];
            config.sectionGenerators.push(this.makeSectionGeneratorConfigFromForm(sectionId, sectionForm));
        }
        return config;
    }

    reset() {
        this.selectedTemplate = undefined;
        this.activeConfiguration = undefined;
    }

    selectTemplate(template: ReportTemplate) {
        this.selectedTemplate = template.id;
        this.activeConfiguration = template.structure;
        this.form = this.makeFormFromConfig(this.activeConfiguration);
    }

    submit() {
        if (this.config.createConfigMode) {
           this.config.onCreateReportConfig(this.makeConfigFromForm(this.form), this.selectedTemplate || '');
        } else {
            // report generation
            this.creatingReport = true;

            if (!this.activeConfiguration) {
                return;
            }

            const currentConfig = this.makeConfigFromForm(this.form);
            currentConfig.renderingConfig = this.activeConfiguration.renderingConfig;

            this.auditService.generateAuditReport(this.config.auditId, currentConfig).subscribe({
                next: reportId => {
                    this.toolService.openTool(PreviewReportToolComponent, new PreviewReportToolConfig(reportId, 'Preview Report'));
                    this.creatingReport = false;
                },
                error: err => {
                    console.error('Error creating report', err);
                    this.submitLabel = "Retry Generating Report";
                    this.infoMessage = "An error occurred while generating the report. Please try again.";
                    this.creatingReport = false;
                }
            });
        }
    }

    deleteTemplate(template: ReportTemplate) {
        this.reportService.deleteReportTemplate$(this.config.projectId, template.id).subscribe(() => {
            this.customTemplates = this.customTemplates.filter(t => t.id !== template.id);
            if (this.selectedTemplate === template.id) {
                this.reset();
            }
        });
    }

    openCreateTemplateDialog() {
        this.toolService.openTool(CreateReportTemplateToolComponent,
            new CreateReportTemplateToolConfig(
                'Create Report Template',
                this.config.projectId,
                "audit",
                undefined,
                (newTemplate) => {
                    this.loading = true;
                    this.reportService.getReportTemplatesForProject$(this.config.projectId).subscribe(templates => {
                        this.baseTemplates = templates.filter(t => t.isBaseTemplate);
                        this.customTemplates = templates.filter(t => !t.isBaseTemplate);
                        this.loading = false;
                        const created = templates.find(t => t.id === newTemplate.id);
                        if (created) this.selectTemplate(created);
                    });
                }
            )
        );
    }

    protected readonly faPlus = faPlus;
    protected readonly faSave = faSave;
    protected readonly faChevronRight = faChevronRight;
    protected readonly faSyncAlt = faSyncAlt;
    protected readonly faPlay = faPlay;
    protected readonly faCheck = faCheck;
    protected readonly faTrash = faTrash;
    protected readonly data = data;
}
